import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/apiClient";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Building2, Users, Target, Loader2, Mail, Plus, Trash2, X, TrendingUp,
  CheckCircle2, Award, BookOpen, ArrowRight, Copy,
} from "lucide-react";

// Format helpers
const acc = (n) => (n == null ? "—" : `${n}%`);

export default function EnterprisePage() {
  const [state, setState] = useState({ loading: true, has_company: false });
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [assignSlug, setAssignSlug] = useState("");
  const [paths, setPaths] = useState([]);

  const load = async () => {
    try {
      const [{ data }, pathsResp] = await Promise.all([
        api.get("/companies/mine"),
        api.get("/paths"),
      ]);
      setPaths(pathsResp.data || []);
      setState({ loading: false, ...data });
    } catch {
      setState({ loading: false, has_company: false });
    }
  };

  useEffect(() => { load(); }, []);

  const createCompany = async () => {
    if (!name.trim()) { toast.error("Give your workspace a name"); return; }
    setCreating(true);
    try {
      await api.post("/companies", { name });
      toast.success("Workspace created — welcome to the Enterprise Portal");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create workspace");
    } finally {
      setCreating(false);
    }
  };

  const invite = async () => {
    if (!inviteEmail.trim()) return;
    try {
      const { data } = await api.post("/companies/mine/invite", { email: inviteEmail.trim() });
      if (data.already_member) toast.info("Already a member of your workspace");
      else if (data.auto_added) toast.success("User was already on LaneX — added to your workspace");
      else toast.success(`Invite queued for ${inviteEmail}`);
      setInviteEmail("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invite failed");
    }
  };

  const revokeInvite = async (email) => {
    await api.delete(`/companies/mine/invite/${encodeURIComponent(email)}`);
    await load();
  };

  const removeMember = async (id) => {
    if (!window.confirm("Remove this member from your workspace?")) return;
    try {
      await api.delete(`/companies/mine/member/${id}`);
      toast.success("Member removed");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove");
    }
  };

  const assignPath = async () => {
    if (!assignSlug) return;
    try {
      await api.post("/companies/mine/assign-path", { path_slug: assignSlug });
      toast.success("Path assigned to all members");
      setAssignSlug("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not assign");
    }
  };

  const unassignPath = async (slug) => {
    await api.delete(`/companies/mine/assign-path/${slug}`);
    await load();
  };

  if (state.loading) {
    return (
      <div className="grid place-items-center py-24 text-muted-foreground">
        <Loader2 className="h-8 w-8 animate-spin" />
        <div className="mt-3 label-mono">Loading workspace…</div>
      </div>
    );
  }

  // -------- No company yet: onboard --------
  if (!state.has_company) {
    return (
      <div data-testid="enterprise-empty" className="mx-auto max-w-2xl space-y-6 py-8 animate-fade-up">
        <div>
          <div className="label-mono text-primary">// enterprise portal</div>
          <h1 className="mt-2 font-display text-3xl font-black tracking-tighter sm:text-4xl">Build a workspace for your team.</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Create a company workspace, invite your employees, assign mandatory learning paths, and see per-employee quiz stats. No credit card, no setup call — try it free.
          </p>
        </div>
        <div className="rounded-2xl border border-primary/40 bg-gradient-to-br from-primary/10 to-transparent p-6">
          <div className="label-mono text-primary">// create workspace</div>
          <div className="mt-4 space-y-3">
            <Input
              data-testid="enterprise-company-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Company name — e.g. Acme Robotics"
              className="h-12 rounded-full"
            />
            <Button
              onClick={createCompany}
              disabled={creating}
              data-testid="enterprise-create-btn"
              className="w-full rounded-full btn-neon font-mono text-xs uppercase tracking-widest"
              size="lg"
            >
              {creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Building2 className="mr-2 h-4 w-4" />}
              {creating ? "Creating…" : "Create workspace"}
            </Button>
          </div>
          <div className="mt-4 grid gap-2 text-xs text-muted-foreground">
            <div className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-accent" /> 25 seats included on the Business plan</div>
            <div className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-accent" /> Assign paths as mandatory training</div>
            <div className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-accent" /> Per-employee compliance & quiz stats</div>
          </div>
        </div>
      </div>
    );
  }

  // -------- Member view: not owner --------
  if (!state.is_owner) {
    return (
      <div data-testid="enterprise-member" className="space-y-6 animate-fade-up">
        <div>
          <div className="label-mono text-primary">// your workspace</div>
          <h1 className="mt-2 font-display text-3xl font-black tracking-tighter">{state.company.name}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            You are a member of this workspace. Assigned paths are highlighted on your dashboard.
          </p>
        </div>
        <Link
          to="/app/paths"
          className="inline-flex items-center gap-2 rounded-full btn-neon px-5 py-2.5 font-mono text-xs uppercase tracking-widest"
        >
          <BookOpen className="h-4 w-4" /> Browse paths <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    );
  }

  // -------- Owner view --------
  const c = state.company;
  const roster = state.roster || [];
  const assigned = state.assigned_paths || [];
  const avgAccuracy = roster.length ? Math.round(roster.reduce((s, r) => s + (r.accuracy || 0), 0) / roster.length) : 0;
  const totalCompletedPaths = roster.reduce((s, r) => s + (r.completed_paths || 0), 0);

  return (
    <div data-testid="enterprise-owner" className="space-y-8 animate-fade-up">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-mono text-primary">// enterprise portal</div>
          <h1 className="mt-2 font-display text-3xl font-black tracking-tighter sm:text-4xl">{c.name}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {c.plan} plan · {c.member_count} of {c.seats} seats used · workspace slug <span className="font-mono text-primary">{c.slug}</span>
          </p>
        </div>
      </div>

      {/* Metrics */}
      <div className="grid gap-4 md:grid-cols-4">
        <div data-testid="ent-metric-seats" className="rounded-2xl border border-primary/40 bg-gradient-to-br from-primary/10 to-transparent p-5">
          <div className="flex items-center justify-between">
            <div className="label-mono">Seats used</div>
            <Users className="h-4 w-4 text-primary" />
          </div>
          <div className="mt-3 font-display text-4xl font-black tracking-tighter">{c.member_count}<span className="text-lg text-muted-foreground"> / {c.seats}</span></div>
          <div className="label-mono">members active</div>
        </div>
        <div data-testid="ent-metric-assigned" className="rounded-2xl border border-border bg-card p-5">
          <div className="flex items-center justify-between">
            <div className="label-mono">Assigned paths</div>
            <Target className="h-4 w-4 text-primary" />
          </div>
          <div className="mt-3 font-display text-4xl font-black tracking-tighter">{assigned.length}</div>
          <div className="label-mono">mandatory learning</div>
        </div>
        <div data-testid="ent-metric-accuracy" className="rounded-2xl border border-border bg-card p-5">
          <div className="flex items-center justify-between">
            <div className="label-mono">Avg quiz accuracy</div>
            <TrendingUp className="h-4 w-4 text-accent" />
          </div>
          <div className="mt-3 font-display text-4xl font-black tracking-tighter">{avgAccuracy}%</div>
          <div className="label-mono">across the roster</div>
        </div>
        <div data-testid="ent-metric-completed" className="rounded-2xl border border-border bg-card p-5">
          <div className="flex items-center justify-between">
            <div className="label-mono">Paths completed</div>
            <Award className="h-4 w-4 text-accent" />
          </div>
          <div className="mt-3 font-display text-4xl font-black tracking-tighter">{totalCompletedPaths}</div>
          <div className="label-mono">total across all members</div>
        </div>
      </div>

      {/* Invite + Assign row */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Invite */}
        <div className="rounded-2xl border border-border bg-card p-6">
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-primary" />
            <h2 className="font-display text-lg font-bold">Invite an employee</h2>
          </div>
          <p className="mt-1 label-mono">If they already have a LaneX account, they&apos;re added instantly.</p>
          <div className="mt-4 flex gap-2">
            <Input
              data-testid="ent-invite-email"
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="employee@company.com"
              className="rounded-full"
            />
            <Button data-testid="ent-invite-btn" onClick={invite} className="rounded-full btn-neon font-mono text-xs uppercase tracking-widest">
              <Plus className="mr-1.5 h-4 w-4" /> Invite
            </Button>
          </div>
          {(c.pending_invites || []).length > 0 && (
            <div className="mt-4 space-y-1.5">
              <div className="label-mono">Pending invites</div>
              {(c.pending_invites || []).map((email) => (
                <div key={email} className="flex items-center justify-between rounded-full border border-border bg-background/60 px-3 py-1.5">
                  <span className="font-mono text-xs truncate">{email}</span>
                  <button
                    onClick={() => revokeInvite(email)}
                    className="rounded-full border border-border p-1 hover:border-destructive/60 hover:text-destructive"
                    title="Revoke invite"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Assign a path */}
        <div className="rounded-2xl border border-border bg-card p-6">
          <div className="flex items-center gap-2">
            <Target className="h-4 w-4 text-primary" />
            <h2 className="font-display text-lg font-bold">Assign a mandatory path</h2>
          </div>
          <p className="mt-1 label-mono">Every member gets the path highlighted in their dashboard.</p>
          <div className="mt-4 flex gap-2">
            <select
              data-testid="ent-assign-select"
              value={assignSlug}
              onChange={(e) => setAssignSlug(e.target.value)}
              className="flex-1 rounded-full border border-border bg-background px-4 py-2 text-sm font-mono"
            >
              <option value="">Choose a path…</option>
              {paths
                .filter((p) => !assigned.find((a) => a.slug === p.slug))
                .map((p) => (
                  <option key={p.slug} value={p.slug}>{p.title}</option>
                ))}
            </select>
            <Button data-testid="ent-assign-btn" onClick={assignPath} disabled={!assignSlug} className="rounded-full btn-neon font-mono text-xs uppercase tracking-widest">
              <Plus className="mr-1.5 h-4 w-4" /> Assign
            </Button>
          </div>
          {assigned.length > 0 && (
            <div className="mt-4 space-y-2">
              <div className="label-mono">Currently assigned</div>
              {assigned.map((p) => (
                <div key={p.slug} className="flex items-center justify-between rounded-xl border border-primary/30 bg-primary/5 p-3">
                  <div className="min-w-0">
                    <div className="truncate font-display text-sm font-bold">{p.title}</div>
                    <div className="label-mono">{p.lesson_count} lessons · mandatory</div>
                  </div>
                  <button
                    onClick={() => unassignPath(p.slug)}
                    className="rounded-full border border-border p-1.5 hover:border-destructive/60 hover:text-destructive"
                    title="Unassign"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Roster */}
      <div data-testid="ent-roster" className="rounded-2xl border border-border bg-card overflow-hidden">
        <div className="flex items-center justify-between border-b border-border p-6">
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-primary" />
            <h2 className="font-display text-lg font-bold">Roster ({roster.length})</h2>
          </div>
          <div className="label-mono hidden sm:block">Ranked by quiz accuracy</div>
        </div>
        {roster.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">No members yet. Invite someone above to get started.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-background/40">
                <tr className="text-left">
                  <th className="p-4 label-mono">Name</th>
                  <th className="p-4 label-mono">Email</th>
                  <th className="p-4 label-mono">Accuracy</th>
                  <th className="p-4 label-mono">Quizzes</th>
                  <th className="p-4 label-mono">Paths done</th>
                  <th className="p-4 label-mono">Compliance</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {roster.map((r) => (
                  <tr key={r.id} className="border-b border-border/50 hover:bg-background/40">
                    <td className="p-4 font-medium">
                      {r.name || "—"}
                      {r.role === "company_admin" && <span className="ml-2 rounded-full border border-accent/40 bg-accent/10 px-2 py-0.5 font-mono text-[0.6rem] uppercase tracking-widest text-accent">Owner</span>}
                    </td>
                    <td className="p-4 font-mono text-xs text-muted-foreground truncate max-w-[220px]">
                      <div className="flex items-center gap-1.5">
                        <span className="truncate">{r.email}</span>
                        <button
                          onClick={() => { navigator.clipboard.writeText(r.email); toast.success("Email copied"); }}
                          className="opacity-0 group-hover:opacity-100 hover:text-primary transition"
                          title="Copy email"
                        ><Copy className="h-3 w-3" /></button>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className={`inline-flex items-center rounded-full border px-2 py-0.5 font-mono text-[0.7rem] ${r.accuracy >= 80 ? "border-accent/40 bg-accent/10 text-accent" : r.accuracy >= 60 ? "border-primary/40 bg-primary/10 text-primary" : "border-destructive/40 bg-destructive/10 text-destructive"}`}>
                        {acc(r.accuracy)}
                      </div>
                    </td>
                    <td className="p-4 font-mono text-xs">{r.quiz_attempts}</td>
                    <td className="p-4 font-mono text-xs">{r.completed_paths}</td>
                    <td className="p-4">
                      {r.assigned_compliance_pct == null ? (
                        <span className="label-mono">n/a</span>
                      ) : (
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 w-16 rounded-full bg-secondary/60 overflow-hidden">
                            <div className="h-full bg-primary" style={{ width: `${r.assigned_compliance_pct}%` }} />
                          </div>
                          <span className="font-mono text-xs">{r.assigned_compliance_pct}%</span>
                        </div>
                      )}
                    </td>
                    <td className="p-4 text-right">
                      {r.role !== "company_admin" && (
                        <button
                          onClick={() => removeMember(r.id)}
                          className="rounded-full border border-border p-1.5 text-muted-foreground hover:border-destructive/60 hover:text-destructive"
                          title="Remove"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
